import React, { useMemo } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Stack,
  Divider,
  DialogTitle,
  IconButton,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";

import type {
  CreateOrderResultUI,
  CreatedOrderUI,
} from "../../../model/order.model";

import { HeaderDialog } from "./HeaderDialog";

import { formatDate, getStatusColor } from "./utilsOrderResultDialog";
import { formatBRL } from "@utils/currency";

type Props = {
  result: CreateOrderResultUI | null;
  onClose: () => void;
  onNewOrder: () => void;
  onConsult: (orderCode?: string) => void;
  resolveProductName?: (productId: string) => string | undefined;
  resolveBranchName?: () => string | undefined;
};

type FailedOrderUI = CreatedOrderUI & { errorMessage: string };

function toFailedOrderUI(f): FailedOrderUI {
  return {
    orderCode: f.orderCode ?? "",
    productId: f.productId,
    deliveryDateISO: f.deliveryDateISO ?? undefined,
    quantityLiters: f.quantityLiters ?? 0,
    price: f.price ?? undefined,
    paymentLabel: f.paymentLabel ?? "-",
    status: f.status ?? "ERRO",
    errorMessage: f.message ?? "Não foi possível criar este item.",
  };
}

export function OrderResultDialogMobile({
  result,
  onClose,
  onNewOrder,
  onConsult,
  resolveProductName,
  resolveBranchName,
}: Props) {
  const open = Boolean(result);

  const createdList = useMemo(() => result?.created ?? [], [result]);

  const failedList = useMemo<FailedOrderUI[]>(
    () => (result?.failed ?? []).map(toFailedOrderUI),
    [result]
  );

  const header = useMemo(() => {
    if (!result) return null;

    if (result.overall === "SUCESSO") {
      return {
        title: "Sucesso!",
        subtitle: "Seu pedido foi efetuado com sucesso!",
        severity: "success",
      };
    }
    if (result.overall === "PARCIAL") {
      return {
        title: "Sucesso parcial",
        subtitle: "Alguns itens foram criados, outros falharam.",
        severity: "warning",
      };
    }
    return {
      title: "Erro ao criar pedido",
      subtitle: "Não foi possível processar o pedido. Confira os detalhes:",
      severity: "error",
    };
  }, [result]);

  const singleCreated = createdList.length === 1 ? createdList[0] : null;

  if (!open || !result || !header) return null;

  return (
    <Dialog
      className="OrderResultDialogMobile"
      open={open}
      maxWidth={false}
      fullWidth
      onClose={onClose}
      scroll="body"
      PaperProps={{
        sx: {
          m: 0,
          width: "90%",
          height: "90%",
          maxWidth: "100%",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 800,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className="OrderResultDialogMobile-CONTENT" sx={{ py: 4 }}>
        {/* Header */}
        {header && (
          <HeaderDialog
            variant="mobile"
            title={header.title}
            subtitle={header.subtitle}
            severity={header.severity}
          />
        )}

        {/* Pedidos criados (lista) */}
        {createdList.length > 0 && (
          <Box mb={2.5}>
            <Divider sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                color="success.main"
                fontWeight={700}
              >
                PEDIDOS CRIADOS ({createdList.length})
              </Typography>
            </Divider>

            <Stack spacing={2}>
              {createdList.map((order) => (
                <OrderDetailsCard
                  key={order.orderCode}
                  kind="success"
                  order={order}
                  resolveBranchName={resolveBranchName}
                  resolveProductName={resolveProductName}
                />
              ))}
            </Stack>
          </Box>
        )}

        {failedList.length > 0 && (
          <Box mt={2}>
            <Divider sx={{ mb: 2 }}>
              <Typography variant="caption" color="error.main" fontWeight={700}>
                ITENS COM ERRO ({failedList.length})
              </Typography>
            </Divider>

            <Stack spacing={2}>
              {failedList.map((f, idx) => (
                <OrderDetailsCard
                  key={`${f.productId}-${idx}`}
                  kind="error"
                  order={f}
                  errorMessage={f.errorMessage}
                  resolveBranchName={resolveBranchName}
                  resolveProductName={resolveProductName}
                />
              ))}
            </Stack>
          </Box>
        )}

        {/* Ações */}
        <Stack mt={4} spacing={1}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => onConsult(singleCreated?.orderCode)}
          >
            Consultar pedido
          </Button>

          {/* UX: ajuda quando existir mais de 1 pedido criado */}
          {createdList.length > 1 && (
            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
            >
              Foram criadas múltiplas ordens. Consulte pelo número exibido
              acima.
            </Typography>
          )}

          <Button variant="outlined" fullWidth onClick={onNewOrder}>
            Fazer novo pedido
          </Button>
          <Button variant="text" fullWidth onClick={onClose} color="inherit">
            Fechar
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

function OrderDetailsCard({
  kind,
  order,
  errorMessage,
  resolveProductName,
  resolveBranchName,
}: {
  kind: "success" | "error";
  order: CreatedOrderUI;
  errorMessage?: string;
  resolveProductName?: (productId: string) => string | undefined;
  resolveBranchName?: () => string | undefined;
}) {
  const isError = kind === "error";

  return (
    <Stack
      spacing={1.25}
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        bgcolor: isError ? "error.lighter" : "grey.50",
        borderColor: isError ? "error.light" : "grey.200",
      }}
    >
      <Row label="Nº da Ordem" value={order.orderCode || "-"} bold />
      <Row label="Filial" value={resolveBranchName?.() ?? "-"} isTruncated />
      <Row label="Entrega" value={formatDate(order.deliveryDateISO)} />
      <Row
        label="Produto"
        value={resolveProductName?.(order.productId) ?? order.productId}
        isTruncated
      />
      <Row
        label="Quantidade"
        value={`${(order.quantityLiters ?? 0).toLocaleString("pt-BR")} L`}
      />
      <Row
        label="Total"
        value={
          order.price != null
            ? formatBRL(order.price * (order.quantityLiters ?? 0))
            : "-"
        }
      />
      <Row label="Pagamento" value={order.paymentLabel ?? "-"} />
      <Row
        label="Status"
        value={order.status}
        color={getStatusColor(order.status)}
      />

      {isError && errorMessage && (
        <Box>
          <Typography variant="caption" fontWeight={800} color="error.main">
            Motivo
          </Typography>
          <Typography variant="caption" color="error.main" display="block">
            {errorMessage}
          </Typography>
        </Box>
      )}
    </Stack>
  );
}

function Row({
  label,
  value,
  color,
  bold = false,
  isTruncated = false,
}: {
  label: string;
  value: React.ReactNode;
  color?: string;
  bold?: boolean;
  isTruncated?: boolean;
}) {
  return (
    <Box display="flex" justifyContent="space-between" gap={2}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ whiteSpace: "nowrap" }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={bold ? 800 : 600}
        color={color}
        textAlign="right"
        sx={
          isTruncated
            ? {
                display: "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
                wordBreak: "break-all",
              }
            : {}
        }
      >
        {value}
      </Typography>
    </Box>
  );
}
